import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Clock,
  ExternalLink,
  FileText,
  PlayCircle,
  Users,
  Search,
  Eye,
  Check,
  Copy
} from "lucide-react";
import { getCoachCurriculumItems } from "../../services/coachAPI";
import {
  CURRICULUM_WEEKDAYS,
  DAY_DISPLAY,
  GRADE_BANDS,
  SPORT_CATEGORY_OPTIONS,
  getGradeBand
} from "../../utils/curriculumConstants";

/* ─── YouTube helpers ─────────────────────────────────────── */
const getYoutubeVideoId = (url = "") => {
  const value = String(url).trim();
  if (!value) return "";
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([^&#]+)/i,
    /(?:youtube\.com\/embed\/)([^&#?/]+)/i,
    /(?:youtu\.be\/)([^&#?/]+)/i
  ];
  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match?.[1]) return match[1];
  }
  return "";
};
const getEmbedUrl = (youtubeLink = "") => {
  const id = getYoutubeVideoId(youtubeLink);
  return id ? `https://www.youtube.com/embed/${id}` : "";
};
const getThumbnailUrl = (youtubeLink = "") => {
  const id = getYoutubeVideoId(youtubeLink);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "";
};

/* ─── Attachment helpers ──────────────────────────────────── */
const isLikelyImageUrl = (url = "") => {
  const u = String(url).trim().toLowerCase();
  return u.startsWith("http") && (/\.(jpe?g|png|gif|webp|bmp|svg)(\?|#|$)/i.test(u) || /image%2f/i.test(u));
};
const isImageAttachment = (item) => {
  if (!item?.attachmentUrl) return false;
  const name = String(item.attachmentName || "").toLowerCase();
  if (/\.(jpe?g|png|gif|webp|bmp|svg)$/.test(name)) return true;
  return isLikelyImageUrl(item.attachmentUrl);
};
const isLikelyPdfUrl = (url = "") =>
  /\.pdf(\?|#|$)/i.test(String(url).trim().toLowerCase()) || /application%2fpdf/i.test(String(url));
const getLessonThumbnail = (item) => {
  if (item?.attachmentUrl && isImageAttachment(item)) return item.attachmentUrl;
  return getThumbnailUrl(item?.youtubeLink);
};
const parseBullets = (text) =>
  String(text || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
const cleanTitle = (title) => String(title || "").replace(/^\[TEST\]\s*/i, "").trim() || "Lesson";

/* ─── Week / day helpers ─────────────────────────────────── */
const weekSortKey = (label) => {
  const m = String(label || "").trim().match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 999;
};
const DEFAULT_WEEK = "Program";
const getWeek = (item) => String(item?.weekLabel || item?.week_label || "").trim() || DEFAULT_WEEK;
const getDay = (item) => {
  const d = String(item?.dayOfWeek ?? item?.day_of_week ?? item?.day ?? "").trim();
  return ["Monday","Tuesday","Wednesday","Thursday","Friday","Coach"].includes(d) ? d : "";
};
const isCoachAudience = (item) => String(item?.audience || "").trim() === "All Coaches";

function buildWeekStructure(weekItems) {
  const coach = [];
  const weekdays = new Map(CURRICULUM_WEEKDAYS.map((d) => [d, []]));
  const noDay = [];
  const sorted = [...weekItems].sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
  for (const item of sorted) {
    const day = getDay(item);
    if (day === "Coach" || (!day && isCoachAudience(item))) { coach.push(item); continue; }
    if (day && weekdays.has(day)) weekdays.get(day).push(item);
    else noDay.push(item);
  }
  let slot = 0;
  for (const item of noDay) {
    weekdays.get(CURRICULUM_WEEKDAYS[slot % CURRICULUM_WEEKDAYS.length]).push(item);
    slot++;
  }
  return { weekdays, coach };
}

function buildFlatOrder(items) {
  const byWeek = new Map();
  for (const item of items) {
    const w = getWeek(item);
    if (!byWeek.has(w)) byWeek.set(w, []);
    byWeek.get(w).push(item);
  }
  const weeks = Array.from(byWeek.keys()).sort((a, b) => {
    const d = weekSortKey(a) - weekSortKey(b);
    return d !== 0 ? d : a.localeCompare(b);
  });
  const out = [];
  for (const w of weeks) {
    const { weekdays, coach } = buildWeekStructure(byWeek.get(w));
    for (const d of CURRICULUM_WEEKDAYS) out.push(...(weekdays.get(d) || []));
    out.push(...coach);
  }
  return out;
}

/* ─── Progress ───────────────────────────────────────────── */
const getUid = () => {
  try {
    const u = JSON.parse(localStorage.getItem("coachUser") || localStorage.getItem("currentUser") || "{}");
    return u.uid || u.id || "anon";
  } catch { return "anon"; }
};
const PKEY = (uid) => `yau_coach_curriculum_${uid}`;
const loadProg = (uid) => {
  try {
    const d = JSON.parse(localStorage.getItem(PKEY(uid)) || "{}");
    return { completed: d.completed || {}, notes: d.notes || {} };
  } catch { return { completed: {}, notes: {} }; }
};
const saveProg = (uid, d) => { try { localStorage.setItem(PKEY(uid), JSON.stringify(d)); } catch {} };

/* ─── Tags ───────────────────────────────────────────────── */
const SPORT_ICONS = { Soccer:"⚽","Flag Football":"🏈",Cheer:"📣",Basketball:"🏀",Baseball:"⚾",General:"🏷" };

// Blue sport pill — matches image exactly
const SportTag = ({ sport }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-sky-100 text-sky-800 border border-sky-200">
    <span className="text-[11px] leading-none">{SPORT_ICONS[sport] || "🏷"}</span>
    {sport?.trim() || "General"}
  </span>
);

// Purple grade pill "Band 2 (2nd/3rd)" — matches image exactly
const BAND_SUFFIX = { 1:"K/1st", 2:"2nd/3rd", 3:"4th/5th", 4:"Middle School" };
const GradeTag = ({ audience }) => {
  const band = getGradeBand(audience);
  let text = audience?.trim() || "—";
  if (band?.level != null) text = `Band ${band.level} (${BAND_SUFFIX[band.level] || band.short})`;
  else if (band) text = band.label;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-100 text-purple-800 border border-purple-200">
      <Users size={10} className="shrink-0" />
      {text}
    </span>
  );
};

// Duration badge — "5m" gray pill
const DurTag = ({ minutes }) => {
  if (minutes == null || minutes === "" || isNaN(Number(minutes))) return null;
  return (
    <span className="inline-flex items-center gap-0.5 text-[11px] font-medium text-gray-500 bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded">
      {Number(minutes)}m
    </span>
  );
};

/* ══════════════════════════════════════════════════════════ */
const Curriculum = () => {
  const [items, setItems]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [catFilter, setCatFilter]   = useState("all");
  const [search, setSearch]         = useState("");
  const [showAll, setShowAll]       = useState(false);
  // All grades active by default (all ticked in image)
  const [gradeKeys, setGradeKeys]   = useState(() => new Set(["1","2","3","4"]));
  const [noteDraft, setNoteDraft]   = useState("");
  const [noteSaved, setNoteSaved]   = useState(false);
  const uid                         = getUid();
  const [prog, setProg]             = useState(() => loadProg(uid));

  useEffect(() => { setProg(loadProg(uid)); }, [uid]);
  const persist = useCallback((next) => { setProg(next); saveProg(uid, next); }, [uid]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true); setError("");
        const data = await getCoachCurriculumItems();
        setItems(data);
        if (data.length) setSelectedId(data[0].id);
      } catch (e) { setError(e?.message || "Failed to load curriculum."); }
      finally { setLoading(false); }
    })();
  }, []);

  const categories = useMemo(() => {
    const v = Array.from(new Set([...SPORT_CATEGORY_OPTIONS, ...items.map((i) => i?.sportCategory).filter(Boolean)]));
    return v.sort((a, b) => a.localeCompare(b));
  }, [items]);

  const baseFiltered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((item) => {
      const inCat = catFilter === "all" || item?.sportCategory === catFilter;
      const inSearch = !term || [item?.title, item?.shortDescription, item?.audience, item?.sportCategory, item?.weekLabel]
        .some((v) => String(v || "").toLowerCase().includes(term));
      const band = getGradeBand(item?.audience);
      const inGrade = !gradeKeys.size || !band?.level || gradeKeys.has(String(band.level)) ||
        String(item?.audience || "").trim() === "All Coaches";
      return inCat && inSearch && inGrade;
    });
  }, [items, catFilter, search, gradeKeys]);

  const weeksSorted = useMemo(() => {
    const s = new Set(baseFiltered.map(getWeek));
    return Array.from(s).sort((a, b) => { const d = weekSortKey(a) - weekSortKey(b); return d !== 0 ? d : a.localeCompare(b); });
  }, [baseFiltered]);

  const visWeeks = useMemo(() =>
    showAll || weeksSorted.length <= 1 ? weeksSorted : weeksSorted.slice(0, 1),
    [showAll, weeksSorted]);

  const filtered = useMemo(() => {
    if (!visWeeks.length) return baseFiltered;
    const allow = new Set(visWeeks);
    return baseFiltered.filter((i) => allow.has(getWeek(i)));
  }, [baseFiltered, visWeeks]);

  const orderedIds = useMemo(() => buildFlatOrder(filtered).map((i) => i.id), [filtered]);

  const withProg = useMemo(() => filtered.map((i) => ({
    ...i, _done: Boolean(prog.completed[i.id]), _note: prog.notes[i.id] || ""
  })), [filtered, prog]);

  const lesson = withProg.find((i) => i.id === selectedId) || withProg[0] || null;

  useEffect(() => { if (lesson) { setNoteDraft(lesson._note || ""); setNoteSaved(false); } }, [lesson?.id]);
  useEffect(() => {
    if (withProg.length && !withProg.some((i) => i.id === selectedId)) setSelectedId(withProg[0].id);
  }, [withProg, selectedId]);

  const embedUrl = lesson ? getEmbedUrl(lesson.youtubeLink) : "";
  const toggleGrade = (level) => setGradeKeys((prev) => { const n = new Set(prev); if (n.has(level)) n.delete(level); else n.add(level); return n; });
  const toggleDone = (checked) => { if (!lesson) return; persist({ ...prog, completed: { ...prog.completed, [lesson.id]: checked } }); };
  const saveNote = () => {
    if (!lesson) return;
    persist({ ...prog, notes: { ...prog.notes, [lesson.id]: noteDraft.trim() } });
    setNoteSaved(true); setTimeout(() => setNoteSaved(false), 2000);
  };
  const nextLesson = () => {
    if (!lesson) return;
    const idx = orderedIds.indexOf(lesson.id);
    if (idx >= 0 && idx < orderedIds.length - 1) setSelectedId(orderedIds[idx + 1]);
  };
  const nextDisabled = !lesson || orderedIds.indexOf(lesson.id) >= orderedIds.length - 1;

  const grouped = useMemo(() => {
    const map = new Map();
    for (const w of visWeeks) {
      const wItems = withProg.filter((i) => getWeek(i) === w);
      map.set(w, buildWeekStructure(wItems));
    }
    return map;
  }, [withProg, visWeeks]);

  /* ── Grade chip config — exact from image ── */
  // Image shows: ✓K/1 (green check), ✓2/3 (blue check), ✓4/5 (blue check), □3 (copy icon), Middle School (plain)
  const GRADE_CHIP_CONFIG = [
    { level: "1", label: "K/1",          checkColor: "text-emerald-500" },
    { level: "2", label: "2/3",          checkColor: "text-blue-500" },
    { level: "3", label: "4/5",          checkColor: "text-blue-500" },
    { level: "4", label: "Middle School",checkColor: "text-blue-500" },
  ];

  /* ── render ── */
  return (
    <div>
      {/* ── Row 1: "This Week's Curriculum" heading + Show All Weeks toggle ── */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[15px] font-bold text-gray-900 tracking-tight">This Week&apos;s Curriculum</h2>
        {/* Toggle — image shows "Show All Weeks" text + small checkbox, not a pill toggle */}
        <label className="flex items-center gap-1.5 cursor-pointer select-none">
          <span className="text-[12px] text-gray-600">Show All Weeks</span>
          <div
            onClick={() => setShowAll((v) => !v)}
            className={`w-[18px] h-[18px] rounded-sm flex items-center justify-center border-2 transition-colors ${
              showAll ? "bg-blue-600 border-blue-600" : "bg-white border-gray-400"
            }`}
          >
            {showAll && <Check size={10} className="text-white" strokeWidth={3} />}
          </div>
        </label>
      </div>

      {/* ── Row 2: Filter bar — exact from image ── */}
      {/* Layout: ✓K/1  ✓2/3  ✓4/5  [copy icon]3  Middle School  |  [All Categories ▾]  [Search title, category...  🔍] */}
      <div className="flex items-center gap-1 mb-4 flex-wrap">

        {/* Grade band chips */}
        {GRADE_CHIP_CONFIG.map(({ level, label, checkColor }) => {
          const on = gradeKeys.has(level);
          return (
            <button
              key={level}
              type="button"
              onClick={() => toggleGrade(level)}
              className={`inline-flex items-center gap-0.5 px-2 py-[3px] rounded text-[12px] font-medium border transition-colors ${
                on ? "bg-white border-gray-300 text-gray-700" : "bg-white border-gray-200 text-gray-400"
              }`}
            >
              {on && <Check size={11} className={`${checkColor} shrink-0`} strokeWidth={3} />}
              {label}
            </button>
          );
        })}

        {/* Divider + copy badge — matches image "□3" element */}
        <span className="text-gray-300 text-[13px] mx-0.5 select-none">|</span>
        <span className="inline-flex items-center gap-0.5 text-[11px] text-gray-500 border border-gray-200 rounded px-1.5 py-[3px] bg-white">
          <Copy size={10} className="text-gray-400" />
          3
        </span>

        {/* Category select */}
        <div className="relative">
          <select
            value={catFilter}
            onChange={(e) => setCatFilter(e.target.value)}
            className="appearance-none pl-2 pr-6 py-[3px] text-[12px] border border-gray-300 rounded bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <option value="all">All Categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <ChevronDown size={11} className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>

        {/* Search input */}
        <div className="relative flex-1 min-w-[130px]">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, category..."
            className="w-full pl-2 pr-6 py-[3px] text-[12px] border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 placeholder-gray-400 text-gray-700"
          />
          <Search size={12} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* ── Body ── */}
      {loading ? (
        <div className="text-center py-10 text-[13px] text-gray-500">Loading curriculum...</div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-[12px] text-red-700">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <BookOpen className="mx-auto text-gray-300 mb-2" size={30} />
          <p className="text-[13px] text-gray-600">No lessons match your filters.</p>
        </div>
      ) : (
        // Two-column split — left ~40%, right ~60%
        <div className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-4 items-start">

          {/* ══ LEFT ══ */}
          <div className="space-y-0 xl:max-h-[calc(100vh-10rem)] xl:overflow-y-auto xl:pr-0.5">
            {visWeeks.map((weekLabel) => {
              const group = grouped.get(weekLabel);
              if (!group) return null;

              return (
                <div key={weekLabel}>
                  {/* Weekday lesson cards */}
                  {CURRICULUM_WEEKDAYS.map((day) => {
                    const dayItems = group.weekdays.get(day) || [];
                    if (!dayItems.length) return null;
                    const dayName = DAY_DISPLAY[day] || day;

                    return dayItems.map((item) => {
                      const thumb = getLessonThumbnail(item);
                      const isSelected = lesson?.id === item.id;
                      const bullets = parseBullets(item.shortDescription);

                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setSelectedId(item.id)}
                          className={`w-full text-left rounded-lg border mb-2 px-3 py-2.5 transition-all ${
                            isSelected
                              ? "border-blue-400 bg-blue-50/80 ring-1 ring-blue-200"
                              : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                          }`}
                        >
                          {/* Title + icon row */}
                          <div className="flex items-start justify-between gap-1 mb-1">
                            <h4 className="text-[15px] font-bold text-gray-900 leading-snug">
                              {dayName}: {cleanTitle(item.title)}
                            </h4>
                            <div className="flex items-center gap-1 shrink-0 mt-0.5">
                              <Eye size={12} className="text-gray-400" />
                              <Copy size={12} className="text-gray-400" />
                              <DurTag minutes={item.durationMinutes} />
                              {item._done && (
                                <span className="text-[9px] font-bold text-emerald-800 bg-emerald-100 border border-emerald-200 px-1 py-0.5 rounded">Done</span>
                              )}
                            </div>
                          </div>

                          {/* Week label green pill */}
                          {(item.weekLabel || item.week_label) && (
                            <span className="inline-block text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-300 px-2 py-0.5 rounded-full mb-1.5">
                              {item.weekLabel || item.week_label}
                            </span>
                          )}

                          {/* Description */}
                          <div className="text-[12px] text-gray-600 mb-2 space-y-0.5 leading-snug">
                            {bullets.length > 1
                              ? bullets.map((line, i) => (
                                  <div key={i} className="flex items-start gap-1.5">
                                    <span className="mt-[5px] w-[5px] h-[5px] rounded-full bg-gray-400 shrink-0" />
                                    <span>{line}</span>
                                  </div>
                                ))
                              : <p>{item.shortDescription}</p>
                            }
                          </div>

                          {/* Bottom: thumbnail + tags */}
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {thumb && (
                              <div className="w-[50px] h-[34px] rounded overflow-hidden shrink-0 border border-gray-100">
                                <img src={thumb} alt="" className="w-full h-full object-cover" />
                              </div>
                            )}
                            <SportTag sport={item.sportCategory} />
                            <GradeTag audience={item.audience} />
                          </div>
                        </button>
                      );
                    });
                  })}

                  {/* Coach Training */}
                  {group.coach.length > 0 && (
                    <div className="mt-3 pt-1">
                      <h3 className="text-[15px] font-bold text-gray-900 mb-1.5">Coach Training</h3>
                      {group.coach.map((item) => {
                        const thumb = getLessonThumbnail(item);
                        const isSelected = lesson?.id === item.id;
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => setSelectedId(item.id)}
                            className={`w-full text-left rounded-lg border mb-2 px-3 py-2.5 transition-all ${
                              isSelected
                                ? "border-blue-400 bg-blue-50/80 ring-1 ring-blue-200"
                                : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                            }`}
                          >
                            {(item.weekLabel || item.week_label) && (
                              <p className="text-[11px] font-semibold text-gray-500 mb-1">
                                {item.weekLabel || item.week_label}
                              </p>
                            )}
                            <div className="flex gap-2 items-start">
                              <div className="w-[68px] h-[48px] rounded overflow-hidden shrink-0 bg-gray-900 border border-gray-200">
                                {thumb
                                  ? <img src={thumb} alt="" className="w-full h-full object-cover" />
                                  : <div className="w-full h-full flex items-center justify-center"><PlayCircle size={16} className="text-gray-600" /></div>
                                }
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-1 mb-0.5">
                                  <p className="text-[12px] font-semibold text-gray-900 leading-snug">{cleanTitle(item.title)}</p>
                                  <div className="flex items-center gap-1 shrink-0">
                                    <Eye size={12} className="text-gray-400" />
                                    <DurTag minutes={item.durationMinutes} />
                                  </div>
                                </div>
                                <p className="text-[11px] text-gray-500 leading-snug line-clamp-2 mb-1">
                                  {item.shortDescription}
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  <SportTag sport={item.sportCategory} />
                                  <GradeTag audience={item.audience} />
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ══ RIGHT: detail panel ══ */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden lg:sticky lg:top-4">
            {lesson ? (
              <div>
                {/* Panel heading — "Week 1: Fundamentals" */}
                <div className="px-4 pt-4 pb-2">
                  <h3 className="text-[14px] font-bold text-gray-900">
                    {lesson.weekLabel || lesson.week_label || cleanTitle(lesson.title)}
                  </h3>
                </div>

                {/* Video embed — full width 16:9 */}
                <div className="relative bg-black w-full">
                  {embedUrl ? (
                    <div className="relative">
                      <iframe
                        key={embedUrl}
                        title={lesson.title}
                        src={embedUrl}
                        className="w-full aspect-video block"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      />
                      {/* Bottom overlay — exact from image: dark gradient, white title, gray channel */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 via-black/50 to-transparent px-3 py-2 pointer-events-none">
                        <p className="text-white text-[12px] font-semibold leading-snug line-clamp-1">
                          {cleanTitle(lesson.title)} | Youth Soccer Drills
                        </p>
                        <p className="text-gray-300 text-[10px] mt-0.5 flex items-center gap-1">
                          <span>▶</span>
                          <span>{lesson.sportCategory || "Soccer"} · YouTube</span>
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full aspect-video flex items-center justify-center bg-gray-900 text-gray-500 text-[12px]">
                      No video link for this lesson.
                    </div>
                  )}
                </div>

                {/* Detail content */}
                <div className="px-4 py-3 space-y-3">

                  {/* ✓ Mark as Completed | Next Lesson → */}
                  <div className="flex items-center justify-between gap-2">
                    <label className="inline-flex items-center gap-1.5 text-[12px] font-medium text-gray-800 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={Boolean(prog.completed[lesson.id])}
                        onChange={(e) => toggleDone(e.target.checked)}
                        className="h-[13px] w-[13px] rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      Mark as Completed
                    </label>
                    {/* Violet "Next Lesson →" button — exact from image */}
                    <button
                      type="button"
                      disabled={nextDisabled}
                      onClick={nextLesson}
                      className="inline-flex items-center gap-1 px-4 py-[5px] rounded-md bg-violet-600 text-white text-[12px] font-semibold hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next Lesson
                      <ChevronRight size={14} strokeWidth={2.5} />
                    </button>
                  </div>

                  {/* Objective */}
                  <div>
                    <h4 className="text-[13px] font-bold text-gray-900 mb-0.5">Objective</h4>
                    {(lesson.objective || "").trim()
                      ? <p className="text-[12px] text-gray-700 leading-relaxed">{lesson.objective}</p>
                      : <p className="text-[12px] text-gray-500 italic">No objective set.</p>
                    }
                  </div>

                  {/* Equipment */}
                  <div>
                    <h4 className="text-[13px] font-bold text-gray-900 mb-0.5">Equipment</h4>
                    {parseBullets(lesson.equipment).length > 0
                      ? <ul className="text-[12px] text-gray-700 pl-4 list-disc space-y-0 leading-snug">
                          {parseBullets(lesson.equipment).map((l, i) => <li key={i}>{l}</li>)}
                        </ul>
                      : <p className="text-[12px] text-gray-500 italic">No equipment listed.</p>
                    }
                  </div>

                  {/* Key Coaching Points */}
                  <div>
                    <h4 className="text-[13px] font-bold text-gray-900 mb-0.5">Key Coaching Points</h4>
                    {parseBullets(lesson.keyCoachingPoints).length > 0
                      ? <ul className="text-[12px] text-gray-700 pl-4 list-disc space-y-0 leading-snug">
                          {parseBullets(lesson.keyCoachingPoints).map((l, i) => <li key={i}>{l}</li>)}
                        </ul>
                      : <p className="text-[12px] text-gray-500 italic">No coaching points listed.</p>
                    }
                  </div>

                  {/* Attachment */}
                  {lesson.attachmentUrl && (
                    <div>
                      <h4 className="text-[13px] font-bold text-gray-900 mb-0.5">Attachment</h4>
                      {isImageAttachment(lesson)
                        ? <div className="rounded-lg overflow-hidden border border-gray-200">
                            <img src={lesson.attachmentUrl} alt="" className="w-full max-h-44 object-contain" loading="lazy" />
                          </div>
                        : <a href={lesson.attachmentUrl} target="_blank" rel="noopener noreferrer"
                            className="flex items-center justify-between rounded border border-gray-200 px-2.5 py-1.5 hover:bg-gray-50 text-[12px]">
                            <span className="flex items-center gap-1.5 text-blue-700 font-medium">
                              <FileText size={12} />
                              {lesson.attachmentName || "Open attachment"}
                            </span>
                            <ExternalLink size={11} className="text-gray-400" />
                          </a>
                      }
                    </div>
                  )}

                  {/* Resources */}
                  {Array.isArray(lesson.resourceLinks) && lesson.resourceLinks.length > 0 && (
                    <div>
                      <h4 className="text-[13px] font-bold text-gray-900 mb-0.5">Resources</h4>
                      <div className="space-y-1">
                        {lesson.resourceLinks.map((link, i) => (
                          <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                            className="flex items-center justify-between rounded border border-gray-200 px-2.5 py-1.5 hover:bg-gray-50 text-[12px]">
                            <span className="text-blue-700 font-medium">{link.label || "Open Resource"}</span>
                            <ExternalLink size={11} className="text-gray-400" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notes — inline "Add Notes..." input + "Save Note" button side-by-side, exact from image */}
                  <div className="flex items-center gap-1.5 pt-2 border-t border-gray-100">
                    <input
                      type="text"
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveNote(); }}
                      placeholder="Add Notes..."
                      className="flex-1 min-w-0 px-2 py-[5px] border border-gray-300 rounded text-[12px] focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white placeholder-gray-400"
                    />
                    <button
                      type="button"
                      onClick={saveNote}
                      className={`shrink-0 px-3 py-[5px] rounded text-[12px] font-semibold transition-colors ${
                        noteSaved ? "bg-emerald-600 text-white" : "bg-violet-600 text-white hover:bg-violet-700"
                      }`}
                    >
                      {noteSaved ? "Saved!" : "Save Note"}
                    </button>
                  </div>

                </div>
              </div>
            ) : (
              <div className="p-10 text-center text-gray-400">
                <BookOpen size={30} className="mx-auto mb-2 text-gray-300" />
                <p className="text-[12px]">Select a lesson to view details</p>
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default Curriculum;